import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api/phx";
import { getDomainConfig } from "../../config/config";
import { useCart } from "../../hooks/useCart";
import StandardButton from "../UI/StandardButton";
import StandardFieldset from "../UI/StandardFieldset";
import StandardFormItem from "../UI/StandardFormItem";
import StandardH2 from "../UI/StandardH2";
import StandardInputWrapper from "../UI/StandardInputWrapper";
import StandardLabel from "../UI/StandardLabel";
import StandardValidation from "../UI/StandardValidation";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { ABContext } from "../../contexts/ab-context";
import { ReactComponent as BfBoltIcon } from "../../images/black-friday/bolt-neon.svg";
import { ReactComponent as BfTextIcon } from "../../images/black-friday/bf-text.svg";
import { ReactComponent as WDWTicketIcon } from "../../images/global/wdw-ticket.svg";

const DiscountForm = ({ isBlackFridayPeriod }) => {
  const config = getDomainConfig();

  const { t } = useTranslation("common");

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("DiscountForm", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const abContext = useContext(ABContext);
  const [discountAdded, setDiscountAdded] = useState("");
  const [cartRefetch, setCartRefetch] = useState(false);
  const { data: cartContents, refetch: cartRefresh } = useCart();

  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if (config.brand === "Atd" && abContext.cro1381 !== undefined)
      setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  // EBO promotion
  const [isWdwPromoActive, setIsWdwPromoActive] = useState(false);

  useEffect(() => {
    // Set EBO promotion date range
    const wdwStart = new Date('April 28, 2025 23:59:59 GMT').getTime();
    const wdwEnd = new Date('November 6, 2025 00:01:00 GMT').getTime();
    const currentTimestamp = new Date().getTime();

    // only for certain WDW resorts
    let isWdwResort = false;
    if (cartContents?.data?.tickets) {
      cartContents.data.tickets.forEach((ticket) => {
        if (ticket.data.resort_id === "481") {
          isWdwResort = true;
        }
      });
    }

    if (document.cookie.includes("show-ebo=1")) {
      setIsWdwPromoActive(isWdwResort && config.brand === "Atd");
      return;
    }

    const isWithinWdwDateRange = currentTimestamp >= wdwStart && currentTimestamp <= wdwEnd;

    setIsWdwPromoActive(isWithinWdwDateRange && isWdwResort && config.brand === "Atd");
  }, [config.brand, cartContents]);

  useEffect(() => {
    if (isWdwPromoActive) {
      setShowDiscountForm(true);
    }
  }, [isWdwPromoActive]);
  // EBO promotion end

  const discountCodeRef = useRef(null);

  // AB tests
  const [isCro1834, setIsCro1834] = useState(false);

  useEffect(() => {
    if (config.brand === "Atd" && abContext.cro1834 !== undefined)
      setIsCro1834(abContext.cro1834.variant);
  }, [abContext]);

  const discountAmount = useRef(cartContents?.meta.total_discount);

  const [loading, setLoading] = useState(false);
  const [codeAlreadyApplied, setCodeAlreadyApplied] = useState(false);

  const handleDiscountFormClick = (event) => {
    event.preventDefault();
    setDiscountAdded("");
    setCodeAlreadyApplied(false);

    const discountCode = discountCodeRef.current?.value;
    if (discountCode) {
      setLoading(true);
      API.post("/cart/discounts", { discount_code: discountCode })
        .then(
          async (discountResponse) => {
           await cartRefresh();
            setCartRefetch(true);
          },
          (error) => {
            console.warn("Error adding discount code:", error);
            // Show fail message.
            setDiscountAdded(false);
          }
        )
        .finally(() => {
          setLoading(false);
          setCartRefetch(false);
        });
    }
  };

  useEffect(() => {
    if (cartRefetch) {
      if (cartContents.meta.total_discount > 0) {
        if (cartContents.meta.total_discount > discountAmount.current) {
          setDiscountAdded(true);
          discountAmount.current = cartContents.meta.total_discount;
          googleDataLayer.createDiscountFormEvent(
            discountCodeRef.current?.value,
            cartContents.meta.total_discount
          );
          return;
        } else if (cartContents.meta.total_discount === discountAmount.current) {
          setDiscountAdded("");
          const isDiscountApplied = cartContents.data.discounts.find(discount => discount.code.toUpperCase() === discountCodeRef.current?.value.toUpperCase());
          if (!isDiscountApplied) {
            setDiscountAdded(false);
          }
          setCodeAlreadyApplied(isDiscountApplied);
          return;
        }
      }
      setDiscountAdded(false);
    }
  }, [cartContents, cartRefetch]);

  const [showCro1381DiscountForm, setShowCro1381DiscountForm] = useState(true); // Change this to false to hide "Enter Discount code" for AB test later in future - as per Defne

  const handleCro1381DiscountFormClick = () => {
    if (isCro1381) {
      setShowCro1381DiscountForm(!showCro1381DiscountForm);
    }
  };

  const [showDiscountForm, setShowDiscountForm] = useState(isBlackFridayPeriod); // Discount form show/hide

  const toggleDiscountForm = () => {
    setShowDiscountForm(!showDiscountForm);
  };

  const renderDiscountContent = () => (
    <>
      {config.brand !== "Atd" && (
        <StandardH2 className={isCro1381 && "cro1381H2"}>
          {t("discount-code")}
        </StandardH2>
      )}

      {(isCro1381 ? showCro1381DiscountForm : true) && (
        <StandardFormItem className="textItem" id="form-item-discount-code">
          {config.brand !== "Atd" && !isCro1381 && !isWdwPromoActive && (
            <StandardLabel htmlFor={"discount-code"}>
              {t("enter-the-discount-code")}
            </StandardLabel>
          )}
          <StandardInputWrapper
            className={`textWrapper ${
              isCro1381 ? "cro1381DiscountWrapper" : ""
            } ${isWdwPromoActive ? "wdwDiscountWrapper" : ""}  ${
              isBlackFridayPeriod ? "bfDiscountWrapper" : ""
            }`}
          >
            <input
              className={styles.textInput}
              ref={discountCodeRef}
              name="discount-code"
              id="discount-code"
            />
            <StandardButton
              className={`${
                isWdwPromoActive ? "wdwApplyCode" : ""
              } sideButton discountButton`}
              id="discount-code-apply"
              loading={loading}
              value="Apply code"
              onClick={handleDiscountFormClick}
            >
              {isCro1381 ? t("cro1381apply") : t("apply-code")}
            </StandardButton>
            {isBlackFridayPeriod && (
              <div className={styles.bfContainer}>
                <div className={styles.bfTitleContainer}>
                  <BfBoltIcon />
                  <BfTextIcon />
                </div>
                <div className={styles.bfWrapContainer}>
                  <div className={styles.bfContentContainer}>
                    {t("back-friday-discount-message")} &nbsp;
                  </div>
                  <div className={styles.bfCodeContainer}>
                    {" "}
                    <span>BLACKFRIDAY</span>
                  </div>
                </div>
              </div>
            )}
            {isWdwPromoActive && (
              <div className={styles.wdwContainer}>
                <div className={styles.wdwWrapContainer}>
                  <div className={styles.wdwContentContainer}>
                    <div className={styles.wdwIconholder}><WDWTicketIcon /></div>
                    <div className={styles.wdwContent}>{t("wdw-discount-message")}{" "}<span>WDW10</span></div>
                  </div>
                </div>
              </div>
            )}
          </StandardInputWrapper>
        </StandardFormItem>
      )}
      {discountAdded && (
        <StandardValidation className={`${!isCro1381 && "flex"} input`} icon="true" valType="success">
          {t("the-discount-code-was-applied")}
        </StandardValidation>
      )}
      {!discountAdded && discountAdded !== "" && (
        <StandardValidation className={`${!isCro1381 && "flex"} input`} icon="true" valType="error">
          {t("discount-code-does-not-exist-or-is-not-valid")}
        </StandardValidation>
      )}
      {codeAlreadyApplied && (
        <StandardValidation className={`${!isCro1381 && "flex"} input`} icon="true" valType="error">
          {t("discount-code-was-applied")}
        </StandardValidation>
      )}
    </>
  );

  return (
    <StandardFieldset
      className={isCro1381 ? "cro1381DiscountPane" : ""}
      id="discount-pane"
    >
      {config.brand === "Atd" ? (
        <>
          <div
            className={`${styles["discountTrigger"]} ${
              showDiscountForm ? styles["open"] : ""
            }`}
            onClick={toggleDiscountForm}
          >
            <p>{t("show-discount-component")}</p>
          </div>
          {showDiscountForm && renderDiscountContent()}
        </>
      ) : (
        renderDiscountContent()
      )}
    </StandardFieldset>
  );
};

export default DiscountForm;
